'use client';

import React, { useRef, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { theme } from '../../config/theme';
import { Chart } from './Chart';
import { ChartSkeleton } from './ChartSkeleton';
import { ChartHamburgerMenu } from './combined/ChartHamburgerMenu';
import { Breadcrumbs } from '../ui-components/Breadcrumbs/Breadcrumbs';
import type { ChartConfig, ChartData, ChartLayout } from '../../types/chart';
import { CustomLegend } from './combined/CustomLegend';
import { Skeleton } from '../skeletons';

// Custom tooltip component for LineChart
const LineChartTooltip = ({ 
  active, 
  payload, 
  label, 
  threshold,
  tooltipLabel,
  tooltipFormatter
}: {
  active?: boolean;
  payload?: readonly any[];
  label?: string | number;
  threshold?: number;
  tooltipLabel?: string;
  tooltipFormatter?: (value: number) => string;
}) => {
  // Helper function to format response time value (default formatter)
  const formatResponseTime = (valueMs: number): string => {
    if (valueMs >= 1000) {
      const seconds = valueMs / 1000;
      const formatted = seconds % 1 === 0 ? seconds.toString() : seconds.toFixed(1);
      return `${formatted} s`;
    }
    return `${Math.round(valueMs)} ms`;
  };

  // Helper function to determine color based on threshold
  const getValueColor = (value: number, thresholdValue?: number): string => {
    if (thresholdValue !== undefined && thresholdValue !== null) {
      if (value >= thresholdValue) {
        return theme.colors.error;
      } else if (value >= thresholdValue * 0.7) {
        return theme.colors.warning;
      } else {
        return theme.colors.success;
      }
    }
    return theme.colors.gray[900];
  };

  if (active && payload && payload?.length > 0) {
    // Format the label - if it already has AM/PM, use it as-is; otherwise parse and format
    let dateTimeLabel = label;
    if (label && typeof label === 'string') {
      const hasAMPM = /AM|PM/i.test(label);
      
      if (hasAMPM) {
        dateTimeLabel = label;
      } else {
        const dateMatch = label.match(/([A-Za-z]{3}\s+\d{1,2},\s+)?(\d{1,2}):(\d{2})/);
        if (dateMatch) {
          try {
            const dateStr = dateMatch[0];
            let date: Date;
            if (dateMatch[1]) {
              date = new Date(dateStr);
            } else {
              const [hours, minutes] = dateStr?.split(':').map(Number);
              date = new Date();
              date.setHours(hours, minutes, 0, 0);
            }
            
            if (dateMatch[1]) {
              dateTimeLabel = date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              });
            } else {
              dateTimeLabel = date.toLocaleString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              });
            }
          } catch (e) {
            dateTimeLabel = label;
          }
        }
      }
    }
    
    // If multiple series, show all values (for threshold breach vs http errors)
    if (payload.length > 1) {
      return (
        <TooltipContainer>
          {dateTimeLabel && (
            <TooltipTitle>{dateTimeLabel}</TooltipTitle>
          )}
          {payload.map((item, index) => {
            const value = typeof item.value === 'number' ? item.value : parseFloat(String(item.value)) || 0;
            let seriesName = item.name || item.dataKey || `Series ${index + 1}`;
            
            // Map series names to user-friendly labels
            if (seriesName === 'Threshold Breach') {
              seriesName = 'Threshold breaches';
            } else if (seriesName === 'HTTP Status Errors') {
              seriesName = 'Http errors';
            }
            
            const seriesColor = item.color || theme.colors.gray[900];
            
            return (
              <TooltipRow key={index}>
                <TooltipLabel>{seriesName}</TooltipLabel>
                <TooltipValue $color={seriesColor}>
                  {value.toLocaleString()}
                </TooltipValue>
              </TooltipRow>
            );
          })}
        </TooltipContainer>
      );
    }
    
    // Single series - use existing logic
    let selectedPayload = payload[0];
    
    if (threshold !== undefined && threshold !== null) {
      const aboveThresholdPayload = payload.find(p => {
        const value = typeof p.value === 'number' ? p.value : parseFloat(String(p.value)) || 0;
        return value >= threshold;
      });
      if (aboveThresholdPayload) {
        selectedPayload = aboveThresholdPayload;
      }
    }
    
    const value = selectedPayload.value;
    const numericValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    const valueColor = getValueColor(numericValue, threshold);
    
    // Use custom formatter if provided, otherwise use default response time formatter
    const displayValue = tooltipFormatter 
      ? tooltipFormatter(numericValue)
      : formatResponseTime(numericValue);
    
    // Use custom label if provided, otherwise default to "Response time"
    const displayLabel = tooltipLabel || 'Response time';
    
    return (
      <TooltipContainer>
        {dateTimeLabel && (
          <TooltipTitle>{dateTimeLabel}</TooltipTitle>
        )}
        <TooltipRow>
          <TooltipLabel>{displayLabel}</TooltipLabel>
          <TooltipValue $color={valueColor}>
            {displayValue}
          </TooltipValue>
        </TooltipRow>
      </TooltipContainer>
    );
  }
  
  return null;
};

const TooltipContainer = styled.div`
  background: ${theme.colors.white};
  border: 1px solid #D9D9DC;
  border-radius: 8px;
  padding: 8px;
  box-shadow: ${theme.shadows.md};
`;

const TooltipTitle = styled.div`
  font-size: 13px;
  font-family: 'Open Sans', sans-serif;
  font-weight: ${theme.fontWeights.bold};
  color: #000000;
  margin-bottom: 10px;
`;

const TooltipRow = styled.div`
  font-size: 12px;
  color: #6E6E6E;
  display: flex;
  justify-content: space-between;
  gap: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TooltipLabel = styled.span`
  font-weight: ${theme.fontWeights.medium};
  font-family: 'Open Sans', sans-serif;
`;

const TooltipValue = styled.span<{ $color: string }>`
  font-weight: ${theme.fontWeights.bold};
  color: ${({ $color }) => $color};
`;

const LineChartContainer = styled.div<{ $isFullscreen: boolean; $hasMinWidth?: boolean }>`
  overflow: visible;
  transition: all 0.3s ease;
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 8px;
  ${({ $hasMinWidth }) => $hasMinWidth ? 'display: inline-block;' : ''}
  
  ${({ $isFullscreen }) =>
    $isFullscreen &&
    `
    height: 100vh;
    width: 100vw;
    display: flex;
    flex-direction: column;
    padding: 40px;
    background: ${theme.colors.white};
  `}
`;

const ChartHeader = styled.div`
  padding: 16px 20px 8px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  flex: 1;
`;


const ChartDescription = styled.p`
  display: block;
  margin: 4px 0 0 0;
  font-size: 0.875rem;
  color: #6b7280;
`;

const ChartContent = styled.div<{ $minHeight?: number, $minWidth?: number }>`
  padding: 8px 16px 0px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: ${({ $minHeight }) => $minHeight ? `${$minHeight}px` : '250px'};
  ${({ $minWidth }) => $minWidth ? `min-width: ${$minWidth}px;` : ''}
  max-height: 100%; /* Prevent flex item from growing beyond container */
  
  /* Chart should take all available space */
  & > div {
    flex: 1;
    height: 100%;
    min-height: 0; /* Ensure chart respects container constraints */
  }
  
  /* Remove hover border */
  &:hover {
    border: none !important;
    outline: none !important;
  }
`;



const ChartContentWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Prevent flex item from growing beyond container */
  height: 100%;
`;


const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 8px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.gray[200]};
  border-top-color: ${theme.colors.primary.accent};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;


export interface LineChartSeries {
  name: string;
  data: number[];
  color?: string;
  strokeWidth?: number;
  strokeDash?: string;
}

export interface LineChartData {
  categories: string[];
  series: LineChartSeries[];
}

/** Optional linear gradient for area fill (top to bottom) */
export interface AreaGradientConfig {
  id: string;
  from: { color: string; opacity: number };
  to: { color: string; opacity: number };
}

export interface LineChartProps {
  title?: React.ReactNode | string;
  description?: string;
  data: LineChartData;
  showArea?: boolean;
  showMarkers?: boolean; // Control whether to show markers on data points
  /** Optional gradient for area fill (e.g. top to bottom). When set, area uses this instead of solid fill. */
  areaGradient?: AreaGradientConfig;
  yAxisTickSuffix?: string; // Suffix for y-axis labels (e.g., 'ms', '%', etc.)
  yAxisTickFormat?: (value: number) => string; // Custom function to format y-axis tick labels
  yAxisLabel?: string; // Label for Y-axis (e.g., 'Error Rate', 'Response Time')
  xAxisLabel?: string; // Label for X-axis (e.g., 'Date', 'Time')
  xAxisTickFormat?: (value: string | number) => string; // Custom function to format x-axis tick labels
  tooltipLabel?: string; // Label for tooltip value (e.g., 'Number of errors', 'Response time')
  tooltipFormatter?: (value: number) => string; // Custom function to format tooltip value
  customTooltip?: React.ComponentType<any>; // Custom tooltip component
  xAxisNTicks?: number; // Number of ticks to show on x-axis (default: 10)
  xAxisTickIndices?: number[]; // Specific indices to show on x-axis (overrides xAxisNTicks)
  height?: number;
  loading?: boolean;
  className?: string;
  minWidth?: number; // Minimum width for horizontal scrolling
  response_time_threshold?: number; // Optional threshold value to display as horizontal red dashed line
  /** Optional fixed y-axis domain [min, max] (e.g. [0, 100] for percentage charts) */
  yAxisDomain?: [number, number];
  quickLinks?: React.ReactNode; // Optional quick links (e.g., time window buttons)
  showLegend?: boolean;
  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  period?: string;
  rightActions?: React.ReactNode; // Optional right actions (e.g., export button)
  /** Called when fullscreen state changes (so parent can avoid unmounting when loading in fullscreen) */
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

export const LineChart: React.FC<LineChartProps> = ({
  title,
  description,
  data,
  showArea = false,
  showMarkers = true,
  areaGradient,
  yAxisTickSuffix,
  yAxisTickFormat,
  yAxisLabel,
  xAxisLabel,
  xAxisTickFormat,
  tooltipLabel,
  tooltipFormatter,
  customTooltip,
  xAxisNTicks = 10,
  xAxisTickIndices,
  height = 400,
  loading = false,
  className,
  minWidth,
  response_time_threshold,
  yAxisDomain,
  quickLinks,
  showLegend = false,
  period,
  style,
  contentStyle,
  rightActions,
  onFullscreenChange,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartKey, setChartKey] = useState(0);
  const onFullscreenChangeRef = useRef(onFullscreenChange);
  onFullscreenChangeRef.current = onFullscreenChange;

  // Monitor fullscreen changes for THIS specific chart only
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!chartRef.current) return;
      
      const doc = document as Document & {
        webkitFullscreenElement?: Element;
        mozFullScreenElement?: Element;
        msFullscreenElement?: Element;
      };
      
      const fullscreenElement = 
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement;
      
      // Only update if THIS chart is the one in fullscreen (or exiting fullscreen)
      const isThisChartFullscreen = fullscreenElement === chartRef.current;
      const wasThisChartFullscreen = isFullscreen;
      
      if (isThisChartFullscreen !== wasThisChartFullscreen) {
        setIsFullscreen(isThisChartFullscreen);
        onFullscreenChangeRef.current?.(isThisChartFullscreen);
        // Force re-render by changing key
        setChartKey(prev => prev + 1);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [isFullscreen]);


  const defaultColors = [
    theme.colors.primary[500],
    theme.colors.success,
    theme.colors.warning,
    theme.colors.error,
    theme.colors.primary[300],
    '#8b5cf6',
  ];

  // Helper function to determine color for response time values
  const getResponseTimeColor = (value: number): string => {
    if (response_time_threshold !== undefined && response_time_threshold !== null) {
      if (value >= response_time_threshold) {
        return theme.colors.error; // Danger (red)
      } else if (value >= response_time_threshold * 0.7) {
        return theme.colors.warning; // Warning (yellow)
      } else {
        return theme.colors.success; // Success (green)
      }
    }
    // Default thresholds if no threshold provided
    if (value >= 5000) {
      return theme.colors.error; // Danger (red)
    } else if (value >= 1000) {
      return theme.colors.warning; // Warning (yellow)
    } else {
      return theme.colors.success; // Success (green)
    }
  };

  const chartData: ChartData[] = data.series.map((series, index) => {
    // Create hovertemplate array with color-coded values per data point
    // Each template in the array corresponds to a data point
    const hovertemplate = series.data.map((value) => {
      const color = getResponseTimeColor(value);
      // Format the value using yAxisTickFormat if provided, otherwise use raw value with suffix
      const formattedValue = yAxisTickFormat 
        ? yAxisTickFormat(value) 
        : yAxisTickSuffix 
          ? `${value}${yAxisTickSuffix}` 
          : value;
      // Use placeholders %{x} for x-axis, but use formatted value directly for y-axis
      return ` <span style="font-size: 12px;">&nbsp;Date & Time :</span> <span style="color:#101018"> %{x}&nbsp;&nbsp;</span><br><span style="font-size: 12px;">&nbsp;&nbsp;Response :</span> <span style="color:${color}">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${formattedValue}&nbsp;&nbsp;</span><extra></extra>`;
    });

    return {
      x: data.categories,
      y: series.data,
      type: 'scatter',
      mode: showMarkers ? 'lines+markers' : 'lines',
      fill: showArea ? 'tonexty' : 'none',
      fillcolor: showArea
        ? (areaGradient ? `url(#${areaGradient.id})` : `${series.color || defaultColors[index]}20`)
        : undefined,
      name: series.name,
      line: {
        color: series.color || defaultColors[index],
        width: series.strokeWidth || 2,
        dash: series.strokeDash ? (series.strokeDash as 'solid' | 'dot' | 'dash' | 'longdash' | 'dashdot' | 'longdashdot') : 'solid',
      },
      marker: showMarkers ? {
        color: series.color || defaultColors[index],
        size: 8,
        line: {
          color: theme.colors.white,
          width: 2,
        },
        opacity: 1,
        symbol: 'circle',
      } : undefined,
      hoverinfo: 'all',
      hovertemplate: hovertemplate?.length > 0 ? hovertemplate[0] : undefined,
    };
  });

  // Calculate y-axis range to include threshold if provided
  // Find min and max values from all data series
  const allDataValues = data.series.flatMap(series => series.data);
  const dataMin = allDataValues?.length > 0 ? Math.min(...allDataValues) : 0;
  const dataMax = allDataValues?.length > 0 ? Math.max(...allDataValues) : 0;
  
  // If fixed domain provided (e.g. [0, 100] for percentage), use it; otherwise compute from data
  let yAxisMax: number;
  let yAxisMin: number;
  if (yAxisDomain && yAxisDomain.length === 2) {
    yAxisMin = yAxisDomain[0];
    yAxisMax = yAxisDomain[1];
  } else {
    // If threshold is provided, ensure y-axis includes it
    yAxisMax = dataMax;
    if (response_time_threshold !== undefined && response_time_threshold !== null) {
      // Add padding above threshold (10% or minimum 50ms)
      const padding = Math.max(response_time_threshold * 0.1, 50);
      yAxisMax = Math.max(dataMax, response_time_threshold + padding);
    } else if (dataMax > 0) {
      // Add 10% padding above max if no threshold
      yAxisMax = dataMax * 1.1;
    }
    // Ensure minimum is at least 0, or slightly below if we have negative values
    yAxisMin = Math.min(0, dataMin * 0.9);
  }

  // Threshold line will be added as a shape in the layout
  // This ensures it spans the full width regardless of data points
  // Prepare shapes array for threshold line
  const shapes: ChartLayout['shapes'] = [];
  if (response_time_threshold !== undefined && response_time_threshold !== null) {
    shapes.push({
      type: 'line',
      x0: 0, // Start at left edge of plot area
      x1: 1, // End at right edge of plot area
      y0: response_time_threshold,
      y1: response_time_threshold,
      line: {
        color: '#D3072A', // Red color
        width: 1,
        dash: 'dash', // Dashed line
      },
    });
  }

  // Adjust height based on fullscreen state
  const chartHeight = isFullscreen && typeof window !== 'undefined' 
    ? window.innerHeight - 150 
    : height;

  // Calculate max value across all series for smart formatting
  const maxValue = Math.max(...data.series.flatMap(s => s.data), 0);
  const minValue = Math.min(...data.series.flatMap(s => s.data.filter(v => v > 0)), 0);

  // Calculate y-axis range with padding for better visualization when data is sparse
  const effectiveMinValue = minValue > 0 ? minValue : 0;
  const dataRange = maxValue - effectiveMinValue;
  
  // Add padding: 15% of range, but ensure minimum padding for very small ranges
  // For very small ranges, use a percentage of the max value to ensure visible padding
  const paddingPercent = 0.15;
  let padding: number;
  
  if (dataRange === 0) {
    // All values are the same - add 20% padding based on the value itself
    padding = maxValue > 0 ? maxValue * 0.2 : 10;
  } else if (dataRange < maxValue * 0.1) {
    // Very small range relative to max value - use percentage of max for better visibility
    padding = maxValue * 0.1;
  } else {
    // Normal case - use percentage of range
    padding = dataRange * paddingPercent;
  }
  
  // Generate custom tick values and labels if custom format function is provided
  // Using Recharts-compatible props
  // Detect if this is count data (small integer values) for better tick generation
  const isCountData = maxValue < 100 && maxValue === Math.floor(maxValue);
  
  let yAxisConfig: Partial<ChartLayout['yaxis']> = {
    // Recharts-specific props
    tickCount: 7, // Number of ticks to display
    domain: [yAxisMin, yAxisMax] as [number | string, number | string], // Axis domain (Recharts format)
    allowDecimals: !isCountData, // Don't allow decimals for count data
    // Legacy props (kept for backward compatibility)
    griddash: 'dash',
    gridcolor: theme.colors.gray[300],
    linecolor: theme.colors.gray[300],
    tickcolor: 'transparent',
    showline: false,
    nticks: 7,
    range: [yAxisMin, yAxisMax], // Set explicit range with padding
    ...(yAxisTickSuffix && { ticksuffix: yAxisTickSuffix }),
  } as any; // Type assertion to allow Recharts props

  if (yAxisTickFormat) {
    // Use custom formatting function with tickvals and ticktext
    // Generate reasonable tick values based on the data range
    // Always include threshold value and maximum value
    const range = maxValue - (minValue > 0 ? minValue : 0);
    const tickCount = 7;
    
    // isCountData is already defined above
    
    let niceStep: number;
    let startValue: number;
    let endValue: number;
    
    if (isCountData) {
      // For count data, use integer-only steps
      const niceRange = Math.ceil(range);
      if (niceRange === 0) {
        niceStep = 1;
      } else if (niceRange <= 5) {
        niceStep = 1; // Step of 1 for small ranges
      } else if (niceRange <= 10) {
        niceStep = 2; // Step of 2 for medium-small ranges
      } else if (niceRange <= 20) {
        niceStep = 5; // Step of 5 for medium ranges
      } else {
        const magnitude = Math.pow(10, Math.floor(Math.log10(niceRange)));
        niceStep = magnitude; // Use magnitude for larger ranges
      }
      
      // Round domain to integers for count data
      const roundedYAxisMax = Math.ceil(yAxisMax);
      startValue = 0; // Always start at 0 for count data
      endValue = Math.ceil(roundedYAxisMax / niceStep) * niceStep;
    } else {
      // Original logic for non-count data (e.g., response times)
      const niceRange = Math.ceil(range);
      const magnitude = Math.pow(10, Math.floor(Math.log10(niceRange)));
      const normalizedRange = niceRange / magnitude;
      
      if (normalizedRange <= 1) niceStep = magnitude / 10;
      else if (normalizedRange <= 2) niceStep = magnitude / 5;
      else if (normalizedRange <= 5) niceStep = magnitude / 2;
      else niceStep = magnitude;
      
      startValue = Math.floor((minValue > 0 ? minValue : 0) / niceStep) * niceStep;
      endValue = Math.ceil(yAxisMax / niceStep) * niceStep;
    }
    
    const tickVals: number[] = [];
    const tickTexts: string[] = [];
    const requiredTicks = new Set<number>();
    
    // Always include threshold value if provided
    if (response_time_threshold !== undefined && response_time_threshold !== null) {
      requiredTicks.add(response_time_threshold);
    }
    
    // Always include maximum value (yAxisMax)
    // For count data, use rounded max to ensure clean integer ticks
    const maxTickValue = isCountData ? Math.ceil(yAxisMax) : yAxisMax;
    requiredTicks.add(maxTickValue);
    
    // Generate regular ticks (but don't limit count yet - we'll add required ticks first)
    for (let val = startValue; val <= endValue; val += niceStep) {
      if (val >= 0) {
        tickVals.push(val);
        tickTexts.push(yAxisTickFormat(val));
      }
    }
    
    // Add required ticks (threshold and max) - these take priority
    // Use tolerance to check if a tick is "close enough" to the required value
    requiredTicks.forEach(requiredVal => {
      if (requiredVal >= 0) {
        // Check if there's already a tick close to this value (within 1% tolerance)
        const tolerance = Math.max(requiredVal * 0.01, 1);
        const closeIndex = tickVals.findIndex(val => Math.abs(val - requiredVal) <= tolerance);
        
        if (closeIndex >= 0) {
          // Replace the close tick with the exact required value
          tickVals[closeIndex] = requiredVal;
          tickTexts[closeIndex] = yAxisTickFormat(requiredVal);
        } else {
          // Add the required tick (even if it's above yAxisMax, we'll adjust the domain)
          tickVals.push(requiredVal);
          tickTexts.push(yAxisTickFormat(requiredVal));
        }
      }
    });
    
    // Sort ticks and remove duplicates
    const tickMap = new Map<number, string>();
    tickVals.forEach((val, index) => {
      tickMap.set(val, tickTexts[index]);
    });
    
    const sortedTickVals = Array.from(tickMap.keys()).sort((a, b) => a - b);
    const sortedTickTexts = sortedTickVals.map(val => tickMap.get(val)!);
    
    // Ensure domain includes all ticks (especially threshold and max)
    const minTick = Math.min(...sortedTickVals);
    const maxTick = Math.max(...sortedTickVals);
    const adjustedYAxisMin = Math.min(yAxisMin, minTick);
    // For count data, ensure domain max is an integer
    const adjustedYAxisMax = isCountData 
      ? Math.ceil(Math.max(yAxisMax, maxTick))
      : Math.max(yAxisMax, maxTick);
    
    yAxisConfig = {
      ...yAxisConfig,
      // Recharts-compatible props
      ticks: sortedTickVals, // Explicit tick values (Recharts prop)
      tickCount: sortedTickVals?.length, // Number of ticks
      domain: [adjustedYAxisMin, adjustedYAxisMax] as [number | string, number | string],
      range: [adjustedYAxisMin, adjustedYAxisMax], // Update range to include all ticks
      // Legacy props (kept for backward compatibility)
      tickmode: 'array',
      tickvals: sortedTickVals,
      ticktext: sortedTickTexts,
      ticksuffix: undefined, // Remove suffix when using custom format
    } as any; // Type assertion to allow Recharts props
  } else if (yAxisTickSuffix && yAxisConfig) {
    // When using suffix, ensure threshold and max are included
    const requiredTicks = new Set<number>();
    
    // Always include threshold value if provided
    if (response_time_threshold !== undefined && response_time_threshold !== null) {
      requiredTicks.add(response_time_threshold);
    }
    
    // Always include maximum value
    requiredTicks.add(yAxisMax);
    
    // Generate ticks based on range
    const range = yAxisMax - yAxisMin;
    const step = range / 6; // Generate ~7 ticks
    const tickVals: number[] = [];
    
    for (let i = 0; i <= 6; i++) {
      const val = yAxisMin + (step * i);
      if (val >= 0) {
        tickVals.push(val);
      }
    }
    
    // Add required ticks if not already present
    // Use tolerance to check if a tick is "close enough" to the required value
    requiredTicks.forEach(requiredVal => {
      if (requiredVal >= 0) {
        // Check if there's already a tick close to this value (within 1% tolerance)
        const tolerance = Math.max(requiredVal * 0.01, 1);
        const closeIndex = tickVals.findIndex(val => Math.abs(val - requiredVal) <= tolerance);
        
        if (closeIndex >= 0) {
          // Replace the close tick with the exact required value
          tickVals[closeIndex] = requiredVal;
        } else {
          // Add the required tick (even if it's above yAxisMax, we'll adjust the domain)
          tickVals.push(requiredVal);
        }
      }
    });
    
    // Sort and remove duplicates
    const sortedTickVals = Array.from(new Set(tickVals)).sort((a, b) => a - b);
    
    // Ensure domain includes all ticks (especially threshold and max)
    const minTick = Math.min(...sortedTickVals);
    const maxTick = Math.max(...sortedTickVals);
    const adjustedYAxisMin = Math.min(yAxisMin, minTick);
    const adjustedYAxisMax = Math.max(yAxisMax, maxTick);
    
    yAxisConfig = {
      ...yAxisConfig,
      ticks: sortedTickVals,
      tickCount: sortedTickVals?.length,
      domain: [adjustedYAxisMin, adjustedYAxisMax] as [number | string, number | string],
      range: [adjustedYAxisMin, adjustedYAxisMax], // Update range to include all ticks
      ticksuffix: yAxisTickSuffix,
    } as any;
  }

  const layout: ChartLayout = {
    height: chartHeight,
    ...(minWidth && { width: minWidth }), // Only set width if minWidth is provided
    autosize: !minWidth, // Enable autosize when minWidth is not set
    // Recharts-compatible margin format (preferred) - using type assertion for Recharts props
    margin: {
      // Recharts format
      top: 10,
      right: 10,
      left: 0,
      bottom: 0,
    } as any, // Type assertion to allow Recharts margin format
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: {
      color: '#6D7280',
      family: 'Open Sans, sans-serif',
    },
    xaxis: {
      // Recharts-specific props (using type assertion)
      type: 'category', // Treat x values as categories
      tickCount: xAxisTickIndices ? xAxisTickIndices?.length : xAxisNTicks, // Number of ticks to display (Recharts prop)
      ticks: xAxisTickIndices, // Specific tick indices to display (if provided)
      domain: [-0.2, data.categories?.length - 0.8] as [number, number], // Axis domain (Recharts format)
      allowDecimals: false, // Don't allow decimals on category axis
      // X-axis tick formatter
      ...(xAxisTickFormat ? {
        tickFormatter: xAxisTickFormat,
      } : {}),
      // X-axis label/title
      ...(xAxisLabel ? {
        title: {
          text: xAxisLabel,
          font: {
            size: 12,
            color: '#6E6E6E',
            family: 'Open Sans, sans-serif',
          },
        },
      } : {}),
      // Legacy props (kept for backward compatibility)
      showline: false,
      gridcolor: 'transparent',
      linecolor: theme.colors.gray[300],
      tickcolor: 'transparent',
      automargin: true,
      range: [-0.2, data.categories?.length - 0.8], // Tighter range to reduce spacing
    } as any, // Type assertion to allow Recharts props
    yaxis: {
      // Recharts-specific props (using type assertion)
      domain: yAxisConfig?.domain ? yAxisConfig.domain : (yAxisConfig?.range ? [yAxisConfig.range[0], yAxisConfig.range[1]] as [number | string, number | string] : undefined),
      // Use tickCount from yAxisConfig if ticks are explicitly provided, otherwise default to 1
      tickCount: (yAxisConfig?.ticks || yAxisConfig?.tickvals) ? (yAxisConfig.tickCount ?? (yAxisConfig?.ticks?.length ?? yAxisConfig?.tickvals?.length ?? 1)) : 1,
      allowDecimals: true, // Allow decimals on Y-axis
      // Custom tick formatting (Recharts-compatible)
      ...(yAxisConfig?.tickvals && yAxisConfig?.ticktext ? {
        ticks: yAxisConfig.tickvals as number[],
        ticktext: yAxisConfig.ticktext,
      } : {}),
      // If ticks are provided directly (not via tickvals), use them
      ...(yAxisConfig?.ticks && !yAxisConfig?.tickvals ? {
        ticks: yAxisConfig.ticks as number[],
      } : {}),
      // Y-axis label/title
      ...(yAxisLabel ? {
        title: {
          text: yAxisLabel,
          font: {
            size: 12,
            color: '#6E6E6E',
            family: 'Open Sans, sans-serif',
          },
        },
      } : {}),
      // Legacy props (kept for backward compatibility)
      ...(yAxisConfig || {}),
    } as any, // Type assertion to allow Recharts props
    legend: {
      // Recharts-specific legend props (using type assertion)
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      // Legacy props (kept for backward compatibility)
      orientation: 'h',
      x: 0.5,
      xanchor: 'center',
      y: -0.25,
      bgcolor: 'transparent',
    } as any, // Type assertion to allow Recharts props
    shapes: shapes?.length > 0 ? shapes : undefined, // Add shapes if threshold is provided
    ...(areaGradient && { areaGradient }),
    hoverlabel: {
      bgcolor: theme.colors.white, // White background
      bordercolor: theme.colors.border.primary, // Primary border color
      font: {
        color: '#101018', // Label color
        family: 'Open Sans, sans-serif',
        size: 12,
        weight: 600,
      },
      align: 'left',
      namelength: 10,
    },
  };

  const config: ChartConfig = {
    // Recharts-specific configuration
    scrollZoom: true, // Enable scroll zoom functionality
    doubleClick: 'reset', // Reset zoom on double click
    responsive: true, // Enable responsive behavior
    // Legacy config (kept for backward compatibility, not used in Recharts)
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
  };

  // Create table data for hamburger menu
  const tableData: Array<Record<string, string | number>> = [];
  data.categories.forEach((category, index) => {
    data.series.forEach(series => {
      tableData.push({
        category,
        series: series.name,
        value: series.data[index]
      });
    });
  });

  // Memoize customTooltip to prevent it from becoming undefined on re-renders
  // Store the current values in refs to prevent stale closures
  const tooltipLabelRef = useRef(tooltipLabel);
  const tooltipFormatterRef = useRef(tooltipFormatter);
  const thresholdRef = useRef(response_time_threshold);

  // Update refs when values change
  React.useEffect(() => {
    tooltipLabelRef.current = tooltipLabel;
    tooltipFormatterRef.current = tooltipFormatter;
    thresholdRef.current = response_time_threshold;
  }, [tooltipLabel, tooltipFormatter, response_time_threshold]);

  // Create a stable customTooltip function that always uses LineChartTooltip
  // This ensures multi-series tooltips (like threshold breach vs http errors) work correctly
  const defaultCustomTooltipComponent = useCallback(
    (props: any) => {
      // Use refs to get current values, preventing stale closures
      const currentLabel = tooltipLabelRef.current;
      const currentFormatter = tooltipFormatterRef.current;
      const currentThreshold = thresholdRef.current;

      return (
        <LineChartTooltip
          {...props}
          threshold={currentThreshold}
          tooltipLabel={currentLabel}
          tooltipFormatter={currentFormatter}
        />
      );
    },
    [] // Empty deps array - function is stable, uses refs for current values
  );

  // Use provided customTooltip if available, otherwise use default LineChartTooltip
  const customTooltipComponent = customTooltip || defaultCustomTooltipComponent;
  const shouldUseCustomTooltip = true;

  if (loading && !isFullscreen) {
    return <ChartSkeleton type="line" height={`${height}px`} />;
  }
  return (
    <LineChartContainer style={style} ref={chartRef} $isFullscreen={isFullscreen} $hasMinWidth={!!minWidth} className={className}>
      {(title || description) && (
        <ChartHeader>
          {(title || description) && (
            <HeaderContent>
              {title && (
                typeof title === 'string' ? (
                  <Breadcrumbs items={[{ label: title }]} style={{ padding: 0 }} />
                ) : (
                  title
                )
              )}
              {description && <ChartDescription>{description}</ChartDescription>}
            </HeaderContent>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {quickLinks}
            {rightActions}
            <ChartHamburgerMenu 
              chartTitle={title || ''}
              tableData={tableData}
              chartContainerRef={chartRef}
            />
          </div>
        </ChartHeader>
      )}
      <ChartContentWrapper>
       <ChartContent 
        $minWidth={minWidth} 
        $minHeight={height}
        style={contentStyle}
      >
            <Chart
              key={`${chartKey}-${period ?? ''}`}
              data={chartData}
              layout={layout}
              config={config}
              revision={chartKey}
              size="small"
              customTooltip={shouldUseCustomTooltip ? customTooltipComponent : undefined}
              showLegend={false}
            />
            {showLegend && (
              <CustomLegend
                series={data.series.map((series, index) => ({
                  name: series.name,
                  color: series.color,
                  data: series.data,
                }))}
               style={{ paddingTop: '24px',paddingBottom: '24px' }}
                size="small"
                legendFontSize="10px"
                legendFontWeight={500}
                defaultColors={[
                  theme.colors.primary[500],
                  theme.colors.success,
                  theme.colors.warning,
                  theme.colors.error,
                  theme.colors.primary[300],
                  '#8b5cf6',
                ]}
              />
            )}
      </ChartContent>

      {loading && isFullscreen && (
          <LoadingOverlay>
            <LoadingSpinner />
          </LoadingOverlay>
        )}
      </ChartContentWrapper>

    </LineChartContainer>
  );
};